import Axios, { GenericAbortSignal, AxiosProgressEvent, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { isMobile } from '@/utils/env';
import Logger from '@/utils/logger';
import { handleWindow } from '@/utils/common';

export enum MethodEnum {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  DELETE = 'delete',
}

/** interfaces */
interface IParams {
  [key: string]: any;
}

export enum ErrorCodes {
  requestLimit = 10031,
  imageNotRegulation = 10063,
  beOccupied = 10051,
  nickNotRegulation = 10053,
  timeout = "ECONNABORTED",
}

export interface IRequestBasicConfig {
  isServerTrigger?: boolean;
  key?: string;
  url: string;
  method?: MethodEnum;
  returnRaw?: boolean;
  skipCsrfToken?: boolean;
  params?: {
    [key: string]: any;
  };
  headers?: {
    [key: string]: any;
  },
  signal?: GenericAbortSignal;
  timeout?: number;
  onUploadProgress?: (progress: AxiosProgressEvent) => void;
}

export interface IRequestErrorInfo {
  code: number;
  message: string;
}

/** utils */
export const commonParams = () => ({
  ...(typeof window == 'undefined' ? {} : {
    device: isMobile ? 'mobile' : 'desktop',
  }),
});

export const removeBlankParam = (params: IParams) => {
  try {
    const rtv: IParams = {};
    Object.keys(params).forEach((key: string) => {
      if (typeof params[key] !== 'undefined' && params[key] !== '') {
        rtv[key] = params[key];
      }
    });
    return rtv;
  } catch (e) {
    return params;
  }
};

/** axios */
const axiosInstance = Axios.create({
  baseURL: '/api/v2',
  headers: {
    'content-type': 'application/json',
  },
  withCredentials: true,
});

axiosInstance.interceptors.request.use(function (config: InternalAxiosRequestConfig) {
  return {
    ...config,
    params: {
      ...removeBlankParam(commonParams()),
      ...removeBlankParam(config.params),
    },
  };
}, function (error) {
  Promise.reject(error);
});

// 添加响应拦截器
axiosInstance.interceptors.response.use(
  function (response) {
    return response?.data ?? {};
  },
  function (error) {
    // 超出 2xx 范围的状态码都会触发该函数。
    return Promise.reject(error);
  }
);

const getCSRFToken = () => axiosInstance({ url: '/csrf-token', method: MethodEnum.GET });

const getCSRFTokenFromCookie = () => Cookies.get('csrf_token_v2');


/** export request method */
const request = ({
  isServerTrigger,
  key = '',
  url,
  headers = {},
  signal,
  onUploadProgress,
  skipCsrfToken = false,
  method = MethodEnum.GET,
  returnRaw = false,
  params = {},
  timeout,
}: IRequestBasicConfig) => {
  let body = {};
  if (params) {
    body = method === MethodEnum.GET ? { params } : { data: params };
  }
  delete headers.host;

  const headersNew = {
    ...axiosInstance.defaults.headers.common,
    ...headers,
  };

  // 服务端
  const { client_ip, referer } = headersNew;
  const user_agent = headersNew['user-agent'];
  const x_trace_id = headersNew['x-kong-trace-id'];

  const serverReqLog = {
    'path': url,
    'method': method,
    'info': key,
    'client_ip': client_ip,
    'user_agent': user_agent,
    'referer': referer,
    'trace_id': x_trace_id,
  };

  const clientReqLog = {
    'path': url,
    'method': method,
    'user_agent': handleWindow().navigator.userAgent,
  };

  const requestLog = isServerTrigger ? serverReqLog : clientReqLog;

  const csrfToken = getCSRFTokenFromCookie();
  const requestService = () => axiosInstance({
    url,
    method,
    signal,
    onUploadProgress,
    ...body,
    headers: {
      ...headersNew,
      ...(method !== MethodEnum.GET && !skipCsrfToken ? { 'X-CSRF-Token': getCSRFTokenFromCookie() || '' } : {})
    },
    timeout,
  })
    .then((data: any) => {
      if (data?.error) { /** 返回 200，但是有业务错误 */
        const { code = -1, message = 'System exception' } = data?.error || {};
        Logger.error({ ...requestLog, message });

        return Promise.reject({
          code,
          message,
          isAppError: true,
          status: 200,
          response: {
            status: 200,
          },
        });
      } else { /** 正常返回 */
        Logger.info({ ...requestLog, 'message': 'successful request' });
        if (returnRaw) {
          return Promise.resolve(data || {});
        }
        return Promise.resolve(data?.data || {});
      }
    })
    .catch((e) => { /** 服务异常 */
      if (e?.response?.data?.error) {
        Logger.error({ ...requestLog, 'message': e.response.data.error });
        return Promise.reject({
          ...e.response.data.error,
          status: e?.response?.status,
          response: {
            status: e?.response?.status,
          },
        });
      }

      Logger.error({ ...requestLog, 'message': e?.message });
      return Promise.reject(e);
    });

  if (method !== MethodEnum.GET && !csrfToken && !skipCsrfToken) {
    return getCSRFToken().then(() => {
      return requestService();
    }).catch((e) => {
      Logger.error({
        'path': '/csrf-token',
        'method': MethodEnum.GET,
        'message': e?.message,
        'client_ip': client_ip,
        'user_agent': user_agent,
        'referer': referer,
        'trace_id': x_trace_id,
      });
      return Promise.reject(e);
    });
  }

  return requestService();
};

export const serverTriggerUrl = `${process.env.SITE_ORIGIN_KUNKKA}/api/v2`;

export default request;
